/**
 * Durable localStorage preferences.
 *
 * Some localStorage entries are user-scoped UI preferences (not auth/session
 * state) that must survive an auth reset. The auth layer (`utils/axios.ts`) calls
 * `localStorage.clear()` on token-refresh failure / logout. Clearing auth state
 * must NOT erase any preferences registered below.
 *
 * Add a prefix here when a key should outlive logout/session-reset.
 */

/** localStorage key prefixes whose values survive `clearAuthLocalStorage()`. */
export const DURABLE_LOCAL_STORAGE_PREFIXES: readonly string[] = [];

function isDurableKey(key: string): boolean {
  return DURABLE_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Clear all localStorage EXCEPT durable UI-preference keys (see
 * `DURABLE_LOCAL_STORAGE_PREFIXES`). Use this everywhere an auth/session reset
 * would otherwise call `localStorage.clear()`, so user preferences aren't lost.
 */
export function clearAuthLocalStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const preserved: Array<[string, string]> = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && isDurableKey(key)) {
        const value = window.localStorage.getItem(key);
        if (value !== null) preserved.push([key, value]);
      }
    }
    window.localStorage.clear();
    preserved.forEach(([key, value]) => window.localStorage.setItem(key, value));
  } catch {
    /* localStorage unavailable — nothing to clear */
  }
}
